import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import {
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useAuthContext } from '../../auth/useAuthContext';
import useMainStore from '../../store/store';
import { useSettingsContext } from '../../components/settings';

const GoogleAuth = ({ isLogin }: { isLogin?: boolean }) => {
  const { themeMode } = useSettingsContext();
  const { googleLogin } = useAuthContext();
  const authTab = useMainStore((s) => s.authTab);
  const [error, setError] = useState<{ message: string } | null>(null);

  // Stores the credential string if we need to ask for a role
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  const executeLogin = async (token: string, selectedRole?: 'publisher' | 'advertiser') => {
    try {
      const response = await googleLogin({
        googleToken: token,
        // If coming from the popup, use selectedRole; otherwise use the current authTab
        userType: selectedRole || (isLogin ? null : authTab),
      });

      if (response.code === 'SIGN_UP_NO_ROLE') {
        setPendingToken(token); // Open the MUI Dialog
      } else {
        setPendingToken(null); // Success, close the dialog
      }
    } catch (err: any) {
      console.error(err);
      const msg = err.status === false ? 'Something went wrong, try again later' : null;
      setError({
        message: err.message || msg || 'An error occurred',
      });
    }
  };

  const handleClose = () => setPendingToken(null);

  return (
    <Stack
      direction="column"
      spacing={1}
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 1 }}
    >
      {!isLogin && (
        <Box sx={{ mb: 2, textTransform: 'uppercase', fontSize: '10px', color: 'text.secondary' }}>
          as {authTab}
        </Box>
      )}
      <GoogleLogin
        onSuccess={async (credentialResponse) => {
          if (credentialResponse?.credential) {
            await executeLogin(credentialResponse.credential);
          }
        }}
        onError={() => {
          console.log('Login Failed');
          setError({ message: 'Google Login Failed' });
        }}
        theme={themeMode === 'dark' ? 'filled_black' : 'outline'}
        text={isLogin ? 'signin_with' : `signup_with`}
      />

      {error?.message && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {error.message}
        </Typography>
      )}

      {/* MUI Role Selection Dialog */}
      <Dialog
        open={!!pendingToken}
        onClose={handleClose}
        maxWidth="xs"
        aria-labelledby="role-selection-title"
      >
        <DialogTitle id="role-selection-title">Select Your Account Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To complete your registration, please tell us how you plan to use the platform.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="info"
              size="large"
              onClick={() => pendingToken && executeLogin(pendingToken, 'publisher')}
            >
              Publisher
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={() => pendingToken && executeLogin(pendingToken, 'advertiser')}
            >
              Advertiser
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default GoogleAuth;
